import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FaPhone, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";

const ContactSection = () => {
  return (
    <ContactContainer id="contact">
      <Content>
      <Title
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          Contact Us
        </Title>
        <ContactGrid>
          {/* Left Column: Form */}
          <FormCard
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <FormTitle>Get in Touch</FormTitle>
            <Form>
              <Input type="text" placeholder="Your Name" required />
              <Input type="email" placeholder="Your Email" required />
              <Input type="text" placeholder="Subject" required />
              <TextArea placeholder="Your Message" rows={5} required />
              <SubmitButton type="submit">Send Message</SubmitButton>
            </Form>
          </FormCard>

          {/* Right Column: Company Details and Map */}
          <DetailsCard
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <DetailsTitle>Contact Details</DetailsTitle>
            <ContactInfo>
              <FaPhone /> 012 012 6148
            </ContactInfo>
            <ContactInfo>
              <FaEnvelope /> info@tersaconstruction.co.za
            </ContactInfo>
            <ContactInfo>
              <FaMapMarkerAlt /> 123 Tersa Street, Johannesburg, South Africa
            </ContactInfo>

            {/* Embedded Map */}
            <MapContainer>
              <iframe
                title="Tersa Construction Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3875.9112480630866!2d27.999615617443865!3d-26.204558599999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1e950dcd5e5c8c4b%3A0x9cd49b7e508e870c!2sJohannesburg!5e0!3m2!1sen!2sza!4v1695123471379!5m2!1sen!2sza"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen={true}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </MapContainer>
          </DetailsCard>
        </ContactGrid>
      </Content>
    </ContactContainer>
  );
};

// Styled Components
const ContactContainer = styled.section`
  background-color: #f3f6fb;
  color: #003366;
  padding: 80px 20px;
`;

const Content = styled.div`
  width: 80%; /* Restrict width to 80% */
  margin: auto; /* Center content horizontally */
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
`;

const Title = styled(motion.h2)`
  font-size: 2.5rem;
  margin-bottom: 40px;
  font-weight: bold;
  text-transform: uppercase;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const FormCard = styled(motion.div)`
  background: #fff;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const FormTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 20px;
  font-weight: bold;
  color: #003366;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const Input = styled.input`
  padding: 10px 15px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #ffa500;
    box-shadow: 0 0 5px rgba(255, 165, 0, 0.5);
  }
`;

const TextArea = styled.textarea`
  padding: 10px 15px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #ffa500;
    box-shadow: 0 0 5px rgba(255, 165, 0, 0.5);
  }
`;

const SubmitButton = styled.button`
  padding: 12px 20px;
  background-color: #003366;
  color: #f3f6fb;
  border: none;
  border-radius: 5px;
  font-size: 1.2rem;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #ffa500;
  }
`;

const DetailsCard = styled(motion.div)`
  background: #fff;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const DetailsTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 20px;
  font-weight: bold;
  color: #003366;
  text-align: center;
`;

const ContactInfo = styled.p`
  font-size: 1.2rem;
  margin-bottom: 15px;
  color: #003366;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const MapContainer = styled.div`
  margin-top: 20px;
  border-radius: 10px;
  overflow: hidden;
`;

const FilterButton = styled.button<{ active: boolean }>`
  padding: 10px 20px;
  background-color: ${({ active }) => (active ? "#ffa500" : "#fff")};
  color: ${({ active }) => (active ? "#fff" : "#003366")};
  border: 1px solid #003366;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  transition: background-color 0.3s, color 0.3s;

  &:hover {
    background-color: #ffa500;
    color: #fff;
  }

  @media (max-width: 768px) {
    padding: 8px 15px; /* Smaller padding for mobile */
    font-size: 0.9rem; /* Slightly smaller font size */
  }

  @media (max-width: 480px) {
    padding: 6px 10px; /* Even smaller padding for very small screens */
    font-size: 0.8rem;
  }
`;


export default ContactSection;
